<template>
  <div class="conges-calendar">
    <div class="calendar-header">
      <div class="month-navigation">
        <button class="btn-icon" @click="previousMonth">
          <span class="material-icons">chevron_left</span>
        </button>
        <h3>{{ currentMonthName }} {{ currentYear }}</h3>
        <button class="btn-icon" @click="nextMonth">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>
      
      <div class="calendar-filters">
        <div class="filter-group">
          <label>Direction:</label>
          <select v-model="filters.directionId">
            <option value="">Toutes</option>
            <option v-for="direction in directions" :key="direction.id" :value="direction.id">
              {{ direction.nom }}
            </option>
          </select>
        </div>
        
        <div class="filter-group">
          <label>Type de congé:</label>
          <select v-model="filters.typeCongeId">
            <option value="">Tous</option>
            <option v-for="type in typesConge" :key="type.id" :value="type.id">
              {{ type.libelle }}
            </option>
          </select>
        </div>
        
        <button class="btn btn-primary" @click="applyFilters">
          <span class="material-icons">filter_alt</span>
          Filtrer
        </button>
      </div>
    </div>
    
    <div class="calendar-view" :class="{'loading': loading}">
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
        <p>Chargement du calendrier...</p>
      </div>
      
      <div class="weekdays">
        <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
      </div>
      
      <div class="calendar-grid">
        <div 
          v-for="(day, index) in calendarDays" 
          :key="index" 
          class="calendar-day" 
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'weekend': day.isWeekend,
            'holiday': day.isHoliday
          }"
        >
          <div class="day-header">
            <span class="day-number">{{ day.date }}</span>
          </div>
          
          <div class="day-content">
            <div 
              v-for="conge in day.conges" 
              :key="conge.id" 
              class="conge-item"
              :class="'conge-status-' + conge.statut.toLowerCase()"
              :style="{ borderLeftColor: getTypeColor(conge.typeCongeId) }"
              @click="showCongeDetails(conge.id)"
            >
              <div class="conge-info">
                <span class="conge-agent">{{ conge.personnel.nom }} {{ conge.personnel.prenom }}</span>
                <span class="conge-type">{{ getTypeName(conge.typeCongeId) }}</span>
              </div>
            </div>
            
            <div v-if="day.moreConges > 0" class="more-conges" @click="showAllConges(day)">
              +{{ day.moreConges }} plus
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal pour afficher tous les congés d'une journée -->
    <dialog ref="dayCongesModal" class="day-conges-modal">
      <div class="modal-header">
        <h3>Congés du {{ selectedDay ? formatDate(selectedDay.fullDate) : '' }}</h3>
        <button class="btn-close" @click="closeDayModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div v-if="selectedDay && selectedDay.conges.length === 0" class="no-conges">
          Aucun congé pour cette journée.
        </div>
        <ul v-else class="conges-list">
          <li 
            v-for="conge in selectedDay ? selectedDay.conges : []" 
            :key="conge.id" 
            class="conge-list-item"
            :class="'conge-status-' + conge.statut.toLowerCase()"
          >
            <div class="conge-color" :style="{ backgroundColor: getTypeColor(conge.typeCongeId) }"></div>
            <div class="conge-details">
              <div class="conge-agent-name">{{ conge.personnel.nom }} {{ conge.personnel.prenom }}</div>
              <div class="conge-info-row">
                <span class="conge-type">{{ getTypeName(conge.typeCongeId) }}</span>
                <span class="conge-dates">{{ formatDateRange(conge.dateDebut, conge.dateFin) }}</span>
              </div>
              <div class="conge-status">
                <span class="status-badge" :class="'status-' + conge.statut.toLowerCase()">
                  {{ getStatusLabel(conge.statut) }}
                </span>
              </div>
            </div>
            <button class="btn-icon" @click="showCongeDetails(conge.id)">
              <span class="material-icons">visibility</span>
            </button>
          </li>
        </ul>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeDayModal">Fermer</button>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useCongeStore } from '@/store/conges';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday, isSameMonth, isWeekend } from 'date-fns';
import { fr } from 'date-fns/locale';

const router = useRouter();
const congesStore = useCongeStore();

// État du composant
const currentDate = ref(new Date());
const loading = ref(false);
const conges = ref([]);
const joursNonOuvrables = ref([]);
const typesConge = ref([]);
const directions = ref([]);
const selectedDay = ref(null);
const dayCongesModal = ref(null);

// Filtres
const filters = reactive({
  directionId: '',
  typeCongeId: ''
});

// Jours de la semaine
const weekdays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Mois courant et année
const currentMonthName = computed(() => {
  return format(currentDate.value, 'MMMM', { locale: fr }).charAt(0).toUpperCase() + 
         format(currentDate.value, 'MMMM', { locale: fr }).slice(1);
});

const currentYear = computed(() => {
  return format(currentDate.value, 'yyyy');
});

// Jours du calendrier
const calendarDays = computed(() => {
  const firstDay = startOfMonth(currentDate.value);
  const lastDay = endOfMonth(currentDate.value);
  
  // Obtenir tous les jours du mois
  const daysInMonth = eachDayOfInterval({ start: firstDay, end: lastDay });
  
  // Déterminer le premier jour à afficher (lundi précédent le premier jour du mois)
  let start = firstDay;
  const dayOfWeek = getDay(firstDay);
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convertir dimanche (0) en 6, lundi (1) en 0, etc.
  if (diff > 0) {
    start = new Date(firstDay);
    start.setDate(start.getDate() - diff);
  }
  
  // Déterminer le dernier jour à afficher (compléter la dernière semaine)
  let end = lastDay;
  const lastDayOfWeek = getDay(lastDay);
  const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
  if (daysToAdd > 0) {
    end = new Date(lastDay);
    end.setDate(end.getDate() + daysToAdd);
  }
  
  // Générer tous les jours à afficher
  const allDays = eachDayOfInterval({ start, end });
  
  // Transformer en objets jour avec les informations nécessaires
  return allDays.map(day => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const dayConges = conges.value.filter(c => {
      const debut = new Date(c.dateDebut);
      const fin = new Date(c.dateFin);
      return day >= debut && day <= fin;
    });
    
    // Limiter à 3 congés affichés par jour
    const visibleConges = dayConges.slice(0, 3);
    const moreConges = dayConges.length > 3 ? dayConges.length - 3 : 0;
    
    return {
      fullDate: day,
      date: format(day, 'd'),
      isCurrentMonth: isSameMonth(day, currentDate.value),
      isToday: isToday(day),
      isWeekend: isWeekend(day),
      isHoliday: joursNonOuvrables.value.some(j => j.date === dayStr),
      conges: visibleConges,
      allConges: dayConges,
      moreConges
    };
  });
});

// Navigation entre les mois
const previousMonth = () => {
  currentDate.value = subMonths(currentDate.value, 1);
  fetchCongesForMonth();
};

const nextMonth = () => {
  currentDate.value = addMonths(currentDate.value, 1);
  fetchCongesForMonth();
};

// Récupération des données
const fetchCongesForMonth = async () => {
  loading.value = true;
  try {
    const firstDay = startOfMonth(currentDate.value);
    const lastDay = endOfMonth(currentDate.value);
    
    const params = {
      dateDebut: format(firstDay, 'yyyy-MM-dd'),
      dateFin: format(lastDay, 'yyyy-MM-dd'),
      ...filters
    };
    
    await congesStore.fetchCongesForCalendar(params);
    conges.value = congesStore.calendarConges || [];
  } catch (error) {
    console.error('Erreur lors du chargement des congés:', error);
  } finally {
    loading.value = false;
  }
};

const fetchTypesConge = async () => {
  try {
    await congesStore.fetchTypesConge();
    typesConge.value = congesStore.typesConge || [];
  } catch (error) {
    console.error('Erreur lors du chargement des types de congé:', error);
  }
};

const fetchDirections = async () => {
  try {
    await congesStore.fetchDirections();
    directions.value = congesStore.directions || [];
  } catch (error) {
    console.error('Erreur lors du chargement des directions:', error);
  }
};

const fetchJoursNonOuvrables = async () => {
  try {
    const year = format(currentDate.value, 'yyyy');
    await congesStore.fetchJoursNonOuvrables(year);
    joursNonOuvrables.value = congesStore.joursNonOuvrables || [];
  } catch (error) {
    console.error('Erreur lors du chargement des jours non ouvrables:', error);
  }
};

// Application des filtres
const applyFilters = () => {
  fetchCongesForMonth();
};

// Gestion des modales
const showAllConges = (day) => {
  selectedDay.value = day;
  if (dayCongesModal.value) {
    dayCongesModal.value.showModal();
  }
};

const closeDayModal = () => {
  if (dayCongesModal.value) {
    dayCongesModal.value.close();
  }
  selectedDay.value = null;
};

const showCongeDetails = (congeId) => {
  router.push(`/personnel/conges/${congeId}`);
};

// Fonctions utilitaires
const getTypeColor = (typeId) => {
  const type = typesConge.value.find(t => t.id === typeId);
  return type ? type.couleur : '#30C7B5';
};

const getTypeName = (typeId) => {
  const type = typesConge.value.find(t => t.id === typeId);
  return type ? type.libelle : 'Congé';
};

const getStatusLabel = (status) => {
  const statusMap = {
    'EN_ATTENTE': 'En attente',
    'APPROUVE': 'Approuvé',
    'REFUSE': 'Refusé',
    'ANNULE': 'Annulé'
  };
  return statusMap[status] || status;
};

const formatDate = (date) => {
  return format(date, 'dd/MM/yyyy');
};

const formatDateRange = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
};

// Cycle de vie du composant
onMounted(async () => {
  await Promise.all([
    fetchTypesConge(),
    fetchDirections(),
    fetchJoursNonOuvrables()
  ]);
  fetchCongesForMonth();
});

// Observer les changements de mois pour mettre à jour les jours non ouvrables
watch(() => format(currentDate.value, 'yyyy'), (newYear, oldYear) => {
  if (newYear !== oldYear) {
    fetchJoursNonOuvrables();
  }
});
</script>

<style scoped>
.conges-calendar {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  margin-bottom: 30px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.month-navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.month-navigation h3 {
  font-size: 1.4rem;
  color: var(--turquoise-deep);
  margin: 0;
  min-width: 180px;
  text-align: center;
}

.btn-icon {
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background-color: #f5f5f5;
  border-color: #d0d0d0;
}

.calendar-filters {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-weight: 500;
  color: #455a64;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: white;
  min-width: 150px;
}

.calendar-view {
  position: relative;
  min-height: 600px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 4px;
}

.weekday {
  text-align: center;
  font-weight: 600;
  color: #455a64;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 6px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  min-height: 120px;
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
}

.other-month {
  opacity: 0.5;
}

.today {
  background-color: #e0f7f4;
  box-shadow: 0 0 0 1px var(--turquoise-primary);
}

.weekend {
  background-color: #f5f5f5;
}

.holiday {
  background-color: #ffebee;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.day-number {
  font-weight: 600;
  color: #455a64;
}

.day-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.conge-item {
  background-color: white;
  border-radius: 4px;
  border-left: 3px solid;
  padding: 4px 8px;
  font-size: 0.8rem;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: transform 0.2s ease;
}

.conge-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.conge-info {
  display: flex;
  flex-direction: column;
}

.conge-agent {
  font-weight: 500;
  color: #455a64;
}

.conge-type {
  font-size: 0.75rem;
  color: #78909c;
}

.more-conges {
  font-size: 0.75rem;
  color: var(--turquoise-primary);
  text-align: center;
  cursor: pointer;
  padding: 2px;
}

.more-conges:hover {
  text-decoration: underline;
}

/* Styles pour les statuts de congé */
.conge-status-en_attente {
  background-color: #fff8e1;
}

.conge-status-approuve {
  background-color: #e8f5e9;
}

.conge-status-refuse {
  background-color: #ffebee;
}

.conge-status-annule {
  background-color: #f5f5f5;
  text-decoration: line-through;
}

/* Modal styles */
.day-conges-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
}

.day-conges-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.day-conges-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.btn-close {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.modal-body {
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.no-conges {
  text-align: center;
  color: #78909c;
  padding: 20px;
}

.conges-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.conge-list-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  background-color: #f9f9f9;
  transition: transform 0.2s ease;
}

.conge-list-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.conge-color {
  width: 12px;
  height: 40px;
  border-radius: 3px;
  margin-right: 12px;
}

.conge-details {
  flex: 1;
}

.conge-agent-name {
  font-weight: 600;
  color: #455a64;
  margin-bottom: 4px;
}

.conge-info-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.conge-type {
  color: #78909c;
}

.conge-dates {
  color: #455a64;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-en_attente {
  background-color: #fff8e1;
  color: #f57c00;
}

.status-approuve {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-refuse {
  background-color: #ffebee;
  color: #c62828;
}

.status-annule {
  background-color: #f5f5f5;
  color: #78909c;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
  border: none;
}

.btn-primary:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #455a64;
  border: 1px solid #e0e0e0;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translate(-50%, -48%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

@media (max-width: 768px) {
  .calendar-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .calendar-filters {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .filter-group select {
    width: 100%;
  }
  
  .weekday {
    font-size: 0.8rem;
    padding: 5px;
  }
  
  .calendar-day {
    min-height: 80px;
  }
  
  .conge-item {
    padding: 2px 4px;
    font-size: 0.7rem;
  }
}
</style>
